import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { HardDrive, Activity, TrendingUp, X, Search, CreditCard, Users, History } from 'lucide-react';
import axios from 'axios';

const PUBLIC_API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/public/`;

export default function PublicDashboard() {
    const [stats, setStats] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    
    // Modal state
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [credentialsForm, setCredentialsForm] = useState({ username: '', password: '' });
    const [modalLoading, setModalLoading] = useState(false);
    

    const fetchData = async () => {
        setLoading(true);
        try {
            const [statsRes, tenantsRes] = await Promise.all([
                axios.get(`${PUBLIC_API}stats/`),
                axios.get(`${PUBLIC_API}tenants/`)
            ]);
            setStats(statsRes.data);
            setTenants(tenantsRes.data);
        } catch (err) {
            console.error("Error al obtener datos", err);
            setError("Sesión expirada o error de conexión.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleOpenAdminModal = async (tenant) => {
        setSelectedTenant(tenant);
        setCredentialsForm({ username: 'Cargando...', password: '' });
        try {
            const response = await axios.get(`${PUBLIC_API}tenants/${tenant.id}/admin_credentials/`);
            setCredentialsForm({ username: response.data.username, password: '' });
        } catch (error) {
            alert('Error cargando credenciales.');
        }
    };

    const handleSubmitAdmin = async (e) => {
        e.preventDefault();
        setModalLoading(true);
        try {
            await axios.post(`${PUBLIC_API}tenants/${selectedTenant.id}/admin_credentials/`, {
                new_username: credentialsForm.username,
                new_password: credentialsForm.password
            });
            alert("Credenciales actualizadas.");
            setSelectedTenant(null);
        } catch (error) {
            alert(error.response?.data?.error || "Error al guardar");
        } finally {
            setModalLoading(false);
        }
    };

    const filteredTenants = tenants.filter(t => 
        t.name.toLowerCase().includes(search.toLowerCase()) || 
        (t.domains && t.domains[0]?.domain.toLowerCase().includes(search.toLowerCase()))
    );

    if (loading && !stats) return <div className="text-center py-20 text-slate-500">Cargando métricas de plataforma...</div>;

    return (
        <div className="space-y-8 pb-10">
            {/* Cabecera Admin */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Dashboard de Administración</h1>
                    <p className="text-slate-500">Administración de Tenants</p>
                </div>
                <div className="bg-white px-4 py-2 rounded-2xl shadow-sm border border-slate-100 text-sm font-medium text-slate-600 flex items-center gap-2">
                    <History size={16} className="text-slate-400" />
                    Última actualización: {new Date().toLocaleTimeString()}
                </div>
            </div>

            {error && (
                <div className="bg-error-50 p-4 rounded-xl text-error-600 font-medium border border-error-100">{error}</div>
            )}

            {/* KPIs Reales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <KPICard 
                    title="Ingresos Estimados (MRR)" 
                    value={`Gs. ${parseFloat(stats?.mrr || 0).toLocaleString()}`} 
                    subtitle="Suma de planes activos" 
                    icon={<TrendingUp size={24} />} 
                    />
                <KPICard 
                    title="Ferreterías Activas" 
                    value={stats?.active_tenants || 0} 
                    subtitle={`${stats?.total_tenants || 0} registradas`} 
                    icon={<HardDrive size={24} />} 
                    />
                <KPICard 
                    title="Peticiones API (Global)" 
                    value="12.4k" 
                    subtitle="Tráfico última hora" 
                    icon={<Activity size={24} />} 
                    />
                <KPICard 
                    title="Planes Premium" 
                    value={stats?.plans_distribution?.find(p => p.name.includes('Premium'))?.count || 0} 
                    subtitle="Clientes de alto valor" 
                    icon={<CreditCard size={24} />} 
                    />
            </div>

            {/* Listado con Buscador */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row justify-between items-center gap-4">
                    <h3 className="font-bold text-slate-800 text-lg">Control de Inquilinos</h3>
                    <div className="relative w-full md:w-80">
                        <Search className="icono-campo" size={18} />
                        <input 
                            type="text"
                            placeholder="Buscar ferretería o dominio..."
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 pl-10 pr-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-500/50 transition-all font-medium"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="overflow-x-auto text-sm">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-slate-50/50 text-slate-500">
                                <th className="tabla-encabezado font-semibold">Nombre</th>
                                <th className="tabla-encabezado font-semibold">Dominio Principal</th>
                                <th className="tabla-encabezado font-semibold">Plan Actual</th>
                                <th className="tabla-encabezado font-semibold">Estado</th>
                                <th className="px-6 py-4 font-semibold uppercase tracking-wider text-right">Gestión</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredTenants.map(t => (
                                <tr key={t.id} className="hover:bg-slate-50/80 transition-colors group">
                                    <td className="px-6 py-4 font-bold text-slate-800">{t.name}</td>
                                    <td className="px-6 py-4 font-mono text-slate-500">
                                        {t.domains && t.domains[0]?.domain}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="px-2.5 py-1 rounded-lg bg-brand-50 text-brand-800 font-bold border border-brand-100">
                                            {t.plan_name || 'Estándar'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <span className={`w-2.5 h-2.5 rounded-full ${t.is_active ? 'bg-exito-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-error-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]'}`} />
                                            <span className="font-semibold text-slate-600">{t.is_active ? 'Activo' : 'Suspendido'}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button 
                                            onClick={() => handleOpenAdminModal(t)}
                                            className="bg-slate-800 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-slate-700 transition-all shadow-sm"
                                        >
                                            Editar Accesos
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredTenants.length === 0 && (
                        <div className="p-20 text-center text-slate-400 font-medium">
                            No se encontraron ferreterías con el término "{search}".
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de Credenciales */}
            {selectedTenant && (
                <div className="fondo-modal">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-[2rem] w-full max-w-sm shadow-2xl overflow-hidden border border-white"
                    >
                        <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                            <div>
                                <h3 className="titulo-modal">Control de Acceso</h3>
                                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">{selectedTenant.name}</p>
                            </div>
                            <button onClick={() => setSelectedTenant(null)} className="p-2 hover:bg-white rounded-full transition-colors shadow-sm">
                                <X size={20} className="text-slate-400" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmitAdmin} className="p-8 space-y-5">
                            <div className="bg-brand-50 text-brand-800 p-4 rounded-2xl text-[11px] font-bold border border-brand-100 leading-relaxed uppercase">
                                Esta acción forzará las credenciales del usuario Administrador Raíz del inquilino. Úselo con responsabilidad.
                            </div>

                            <div>
                                <label htmlFor="panel-username-admin" className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 px-1">Username Admin</label>
                                <div className="relative">
                                    <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                                    <input id="panel-username-admin" required className="w-full bg-slate-50 border border-slate-200 px-11 py-3.5 rounded-2xl focus:ring-2 focus:ring-brand-500/50 outline-none font-bold text-slate-700 transition-all"
                                        value={credentialsForm.username} onChange={e => setCredentialsForm({ ...credentialsForm, username: e.target.value })} />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="panel-nueva-contrasena" className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 px-1">Nueva Contraseña</label>
                                <input id="panel-nueva-contrasena" placeholder="Solo si desea cambiarla" className="w-full bg-slate-50 border border-slate-200 px-5 py-3.5 rounded-2xl focus:ring-2 focus:ring-brand-500/50 outline-none font-bold text-slate-700 transition-all"
                                    value={credentialsForm.password} onChange={e => setCredentialsForm({ ...credentialsForm, password: e.target.value })} />
                            </div>

                            <button type="submit" disabled={modalLoading} className="w-full bg-brand-600 hover:bg-brand-500 text-negro-950 py-4 rounded-2xl shadow-xl shadow-brand-600/20 font-black uppercase text-sm tracking-widest transition-all active:scale-[0.98] disabled:opacity-50">
                                {modalLoading ? 'Sincronizando...' : 'Actualizar Credenciales'}
                            </button>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
}

// Las tarjetas tenian un color por cada una, y una quedaba verde entre tres
// amarillas: el verde ahi no decia "salio bien", solo adornaba. El indicador ya
// se distingue por su titulo y su icono.
function KPICard({ title, value, subtitle, icon }) {
    return (
        <div className="p-6 rounded-3xl border bg-brand-50 text-brand-800 border-brand-100 shadow-sm relative overflow-hidden group">
            <div className="flex justify-between items-start z-10 relative">
                <div>
                    <p className="text-xs font-bold uppercase tracking-widest opacity-70 mb-1">{title}</p>
                    <h4 className="text-2xl font-black text-slate-800">{value}</h4>
                    <p className="text-[10px] font-bold opacity-60 mt-1 uppercase leading-tight">{subtitle}</p>
                </div>
                <div className="bg-white/50 p-3 rounded-2xl shadow-inner group-hover:scale-110 transition-transform">
                    {icon}
                </div>
            </div>
            {/* Decoro radial */}
            <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-white/20 rounded-full blur-3xl group-hover:bg-white/40 transition-all"></div>
        </div>
    );
}
